import PropTypes from "prop-types";
import React from "react";
import {ContextualMenu, IconButton, Modal, SelectionMode, Stack, Text, TooltipHost} from "@fluentui/react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {useTheme} from "../../services/ui/ui-services";
import * as uiActions from "../../services/ui/ui-actions";
import {CvDetailsList} from "../widgets/CvDetailsList";

const entityName = "audit_log";
const timezoneOffsetMillis = new Date().getTimezoneOffset() * 60000;

export const entityNames = {
  "account": "Account",
  "authorization": "Authorizatie",
  "brand": "Label",
  "businessUnit": "Unit",
  "characteristics": "Profiel",
  "cv": "Profiel",
  "education": "Opleiding",
  "experience": "Werkervaring",
  "publication": "Publicatie",
  "reference": "Referentie",
  "skill": "Vaardigheid",
  "training": "Training"
};

const HistoryView = prps => {

  const selectors = useSelector(
    state => ({
      locale: state.ui.userPrefs.locale,
      accountEntity: state.safe.content.account,
      auditLogEntity: state.safe.content[entityName],
      selectedAccountId: state.ui.selectedId.account,
      isHistoryViewVisible: state.ui.isHistoryViewVisible
    }),
    {equalityFn: shallowEqual}
  );
  const dispatch = useDispatch();
  const dispatches = React.useMemo(() => ({
      setHistoryViewVisible: (isVisible) => dispatch(uiActions.setHistoryViewVisible(isVisible))
    }),
    [dispatch]);
  const props = {...prps, ...selectors, ...dispatches};

  const formatTimestampMillis = (timestampMillisUTC, locale) => {
    const date = new Date(timestampMillisUTC - timezoneOffsetMillis);
    return `${date.toLocaleDateString(locale)} ${date.toLocaleTimeString(locale)}`;
  };

  const getAccountName = (accountId) => {
    const account = props.accountEntity?.[accountId] || {};
    return account?.name || "onbekend";
  };

  const summarizeAuditLog = (auditLog) => {
    let performedAction;
    if (!auditLog.oldInstance) {
      performedAction = "toegevoegd";
    } else if (auditLog.newInstance) {
      performedAction = "gewijzigd";
    } else {
      performedAction = "verwijderd";
    }
    return `${entityNames[auditLog.entity]} ${performedAction}`;
  };

  const auditLogContext = React.useMemo(() => ({
    locale: props.locale,
    entity: props.auditLogEntity,
    instanceId: null,
    setSelectedInstanceId: () => {}
  }),
  [props.locale, props.auditLogEntity]);

  // Find all {auditLogs} of the selected {account}.
  const enrichedAuditLogs = React.useMemo(() =>
    props.selectedAccountId && Object.values(props.auditLogEntity || {})
      .filter(instance => instance.cvAccountId === props.selectedAccountId || instance.instanceId === props.selectedAccountId)
      .map(instance => ({
        ...instance,
        timestampMillis: -Date.parse(instance.timestamp), // The minus sign enforces initial decsending sort order.
        who: getAccountName(instance.editorAccountId)
      }))
    || [],
  [props.auditLogEntity, props.accountEntity, props.selectedAccountId]);

  const { viewPaneBackground } = useTheme();

  const onRenderTimestampMillis = React.useCallback(item =>
    formatTimestampMillis(-item.timestampMillis, props.locale?.substring(0, 2)),
  [props.locale]);

  const onRenderWhat = (item) =>
    <TooltipHost
      content={JSON.stringify(item.newInstance || item.oldInstance, null, 2)}
      style={{ whiteSpace: "pre-wrap" }}>
      <Text>{summarizeAuditLog(item)}</Text>
    </TooltipHost>;

  const columns = [
    {
      key: "timestampMillis",
      fieldName: "timestampMillis",
      name: "Wanneer",
      onRender: onRenderTimestampMillis,
      isResizable: false,
      minWidth: 110,
      maxWidth: 110,
      data: "number"
    },
    {
      key: "who",
      fieldName: "who",
      name: "Wie",
      isResizable: true,
      minWidth: 80,
      maxWidth: 180
    },
    {
      key: "entity",
      fieldName: "entity",
      name: "Wat",
      onRender: onRenderWhat,
      isResizable: true,
      minWidth: 150,
      maxWidth: 250
    }
  ];

  const historyViewStyles = {
    root: {
      background: viewPaneBackground,
      padding: 20,
      width: 560,
      height: "calc(100vh - 300px)"
    }
  };

  return (
    <Modal
      isOpen={props.isHistoryViewVisible}
      onDismiss={() => props.setHistoryViewVisible(false)}
      isModeless={false}
      isBlocking={false}
      dragOptions={{
        moveMenuItemText: "Move",
        closeMenuItemText: "Close",
        menu: ContextualMenu
      }}
      styles={{
        root: {
          margin: "-8px",
          overflow: "hidden"
        }
      }}>
      <Stack styles={historyViewStyles}>
        <Stack horizontal horizontalAlign="space-between">
          <Text variant="xxLarge">Wijzigingshistorie</Text>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            onClick={() => props.setHistoryViewVisible(false)}
          />
        </Stack>
        <Text style={{ position: "absolute", top: "120px", left: "132px" }}><em>Er zijn geen recente wijzigingsgegevens beschikbaar.</em></Text>
        <CvDetailsList
          columns={columns}
          items={enrichedAuditLogs}
          instanceContext={auditLogContext}
          selectionMode={SelectionMode.none}
          setKey={entityName}
        />
      </Stack>
    </Modal>
  );
};

HistoryView.propTypes = {
  locale: PropTypes.string,
  accountEntity: PropTypes.object,
  auditLogEntity: PropTypes.object,
  selectedAccountId: PropTypes.string,
  isHistoryViewVisible: PropTypes.bool,
  setHistoryViewVisible: PropTypes.func
};

export default HistoryView;